import React, { useState, useEffect } from "react";
import clsx from "clsx";
import moment from "moment";
import PropTypes from "prop-types";
import {
  CustomDatePicker,
  CustomSelect,
  ValidatingTextField,
} from "../FormComponents";
import { API } from "../../apis/api";
import {
  API_RESOURCE_URLS,
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
} from "../../constants";
import { isPositiveInteger, isQuantityValid } from "../../utils/utils";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";
import { useErrorHandler } from "react-error-boundary";
import { buildErrorMessage } from "../../apis/calls";
import { Modal, Box } from "@material-ui/core";
import styles from "./AddEvrNumberForm.module.css";

const AddEvrNumberForm = ({
  title,
  open,
  handleClose,
  plant,
  lineBackgroundColor,
  cancelButtonText,
  submitButtonText,
}) => {
  const [highlightMandatoryFields, setHighlightMandatoryFields] = useState(false);
  const { showPopup } = usePopupManager();
  const handleError = useErrorHandler();
  const [countryCode, setCountryCode] = useState(null);
  const [colourCode, setColourCode] = useState(null);
  const [vc, setVc] = useState("");
  const [evrQuantity, setEvrQuantity] = useState("");
  const [evrNumber, setEvrNumber] = useState("");
  const [validFromDate, setValidFromDate] = useState(null);
  const [validToDate, setValidToDate] = useState(null);
  const [colours, setColours] = useState({ loading: false, data: [] });
  const [countries, setCountries] = useState({ loading: false, data: [] });

  const currentMonthFirstDate = moment().startOf("month");
  const currentMonthLastDate = moment().endOf("month");

  const getColourOptions = () => {
    return colours.data.map(({ colourCode, colourDescription }) => ({
      value: colourCode,
      label: `${colourDescription} (${colourCode})`,
    }));
  };

  const getCountryCodeOptions = () => {
    return countries.data
      .map(({ code, country }) => ({
        value: code,
        label: `${code} - ${country}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const loadData = async (setState, url, params) => {
    try {
      const response = await API.get(url, {
        params: params,
      });
      setState({ loading: false, data: response.data });
    } catch (error) {
      console.error(buildErrorMessage(error));
      handleError(error);
    }
  };

  const loadCountries = async () => {
    return loadData(setCountries, API_RESOURCE_URLS.COUNTRIES);
  };

  const loadColours = async () => {
    return loadData(setColours, API_RESOURCE_URLS.COLOURS);
  };

  const isAlphanumericNoSpace = (value) => {
    return /^[a-zA-Z0-9]*$/.test(value);
  };

  const handleVcChange = (e) => {
    const value = e.target.value;
    if (isAlphanumericNoSpace(value) || value === "") {
      setVc(value);
    }
  };

  const handleEvrNumberChange = (e) => {
    const value = e.target.value;
    if (isAlphanumericNoSpace(value) || value === "") {
      setEvrNumber(value);
    }
  };

  const runPreSubmissionChecks = () => {
    if (!vc || !validFromDate || !validToDate || !evrQuantity || !evrNumber || !countryCode || !colourCode) {
      setHighlightMandatoryFields(true);
      return false;
    }
    return true;
  };
  

  const handleSubmit = async () => {
    try {
      const response = await API.put(API_RESOURCE_URLS.addEvrNumber(plant.value),{
        plant:plant.value,
        vc: vc,
        evrNumber: evrNumber,
        quantity: Number(evrQuantity),
        countryCode: countryCode.value,
        colourCode: colourCode.value,
        validTo: validToDate,
        validFrom: validFromDate,
      } );
      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: "Evr Number added Successfully.",
        info: `Evr Number: ${evrNumber}`,
      });
      handleClose()
    } catch (error) {
      console.error(buildErrorMessage(error));
      showPopup({
        type: MESSAGE_TYPE.FAILURE,
        contextText: DISPLAY_MESSAGES.EVR_ADD_FAILURE,
        info: "Evr Number addition Failed",
        error: error,
      });
    }
  };

  const resetState = () => {
    setVc("");
    setValidFromDate(null);
    setValidToDate(null);
    setColourCode(null);
    setCountryCode(null);
    setEvrQuantity("");
    setEvrNumber("");
    setHighlightMandatoryFields(false);
  };

  useEffect(() => {
    if (open) {
      loadCountries();
      loadColours();
    } else {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plant]);

  return (
    <Modal open={open} onClose={handleClose} disableBackdropClick>
      <div className={styles.formContainer}>
        <div className={styles.formTitle}>
          <div>
            <span style={{ backgroundColor: "#EAEAEA" }}>{title}</span>
          </div>
          <div
            className={styles.formlLineDiv}
            style={{ backgroundColor: lineBackgroundColor }}
          />
        </div>
        <div className={styles.subHeading}>Note: All fields are mandatory</div>
        
        <Box className={styles.box_Line}>
          <div className={styles.gridcontainer_Basic}>
            <div className={styles.plantDetailsLeftLabel_BasicInfo}>
              <label className={styles.label}>Plant</label>
            </div>
            <div className={styles.lineModalLeftInput_BasicInfo}>
              <input
                aria-label="Plant"
                className={styles.textfield}
                value={plant.label}
                disabled
              />
            </div>
            <div className={styles.plantDetailsLeftLabel_BasicInfo}>
              <label className={styles.label}>VC Number</label>
            </div>
            <div className={styles.lineModalLeftCustomSelect_BasicInfo}>
              <ValidatingTextField
                isMandatory
                validationFn={isAlphanumericNoSpace}
                markIfEmpty={highlightMandatoryFields}
                isQuantityValid={isAlphanumericNoSpace}
                aria-label="Vc-number-input"
                quantityValidationText={DISPLAY_MESSAGES.INVALID_INPUT}
                variant="outlined"
                value={vc}
                onChange={handleVcChange}
                placeholder="(Enter Vc Number)"
                className={clsx(styles.textFieldVc, {
                  [styles.errorField]: highlightMandatoryFields && !vc
                })}
                inputProps={{
                  className: styles.textInput,
                  "data-testid": "Vc-number-input",
                }}
                FormHelperTextProps={{
                  className: styles.helperText,
                }}
                helperText={highlightMandatoryFields}
              />
            </div>
          </div>
        </Box>

        <div className={styles.BasicInfo_formSec}>
          <span className={styles.BasicInfo_rowTitle}>Evr Info</span>
          <Box className={styles.box_BasicInfo}>
            <div className={styles.gridcontainer_VCDetails}>
              <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                <label className={styles.label}>Evr Number</label>
              </div>
              <div className={styles.lineModalLeftInput_BasicInfo}>
                <ValidatingTextField
                  isMandatory
                  validationFn={isAlphanumericNoSpace}
                  markIfEmpty={highlightMandatoryFields}
                  isQuantityValid={isQuantityValid(evrQuantity)}
                  aria-label="Evr-number-input"
                  quantityValidationText={DISPLAY_MESSAGES.INVALID_INPUT}
                  variant="outlined"
                  value={evrNumber}
                  onChange={handleEvrNumberChange}
                  placeholder="(Enter Evr Number)"
                  className={clsx(styles.textFieldEvr, {
                    [styles.errorField]: highlightMandatoryFields && !evrNumber
                  })}
                  inputProps={{
                    className: styles.textInput,
                    "data-testid": "Evr-number-input",
                  }}
                  FormHelperTextProps={{
                    className: styles.helperText,
                  }}
                  helperText={highlightMandatoryFields }
                />
              </div>
              <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                <label className={styles.label}>Quantity</label>
              </div>
              <div className={styles.lineModalLeftInput_BasicInfo}>
                <ValidatingTextField
                  isMandatory
                  validationFn={isPositiveInteger}
                  markIfEmpty={highlightMandatoryFields}
                  isQuantityValid={isQuantityValid(evrQuantity)}
                  aria-label="Quantity-number-input"
                  validationHelperText={
                    DISPLAY_MESSAGES.POSITIVE_NUMBER_VALIDATION_WARNING
                  }
                  quantityValidationText={DISPLAY_MESSAGES.INVALID_INPUT}
                  variant="outlined"
                  value={evrQuantity}
                  onChange={(e) => setEvrQuantity(e.target.value)}
                  placeholder="(Enter Evr Quantity)"
                  className={styles.textField}
                  inputProps={{
                    className: styles.textInput,
                    "data-testid": "Quantity-number-input",
                  }}
                  FormHelperTextProps={{
                    className: styles.helperText,
                  }}
                />
              </div>
            </div>
          </Box>

          <span className={styles.BasicInfo_rowTitle}>Date details</span>
          <Box className={styles.box_BasicInfo}>
            <div className={styles.gridcontainer_VCDetails}>
              <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                <label className={styles.label}>Valid From</label>
              </div>
              <div className={styles.lineModalCustomDatePicker}>
                <CustomDatePicker
                  isMandatory
                  markIfUnselected={highlightMandatoryFields}
                  autoOk
                  inputVariant="outlined"
                  value={validFromDate}
                  onChange={(date) => setValidFromDate(date.format(DATE_FORMAT.ISO))}
                  inputProps={{ className: clsx(styles.select, styles.dateInput) }}
                  emptyLabel="dd/mm/yyyy"
                  minDate={currentMonthFirstDate}
                  format="DD/MM/yyyy"
                  data-testid="date-picker-div"
                />
              </div>
              <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                <label className={styles.label}>Valid To</label>
              </div>
              <div className={styles.lineModalCustomDatePicker}>
                <CustomDatePicker
                  isMandatory
                  markIfUnselected={highlightMandatoryFields}
                  autoOk
                  inputVariant="outlined"
                  value={validToDate}
                  onChange={(date) => setValidToDate(date.format(DATE_FORMAT.ISO))}
                  inputProps={{ className: clsx(styles.select, styles.dateInput) }}
                  emptyLabel="dd/mm/yyyy"
                  minDate={currentMonthLastDate}
                  format="DD/MM/yyyy"
                  data-testid="date-picker-div"
                />
              </div>
            </div>
          </Box>

          <span className={styles.BasicInfo_rowTitle}>Other details</span>
          <Box className={styles.box_Revision}>
            <div className={styles.gridcontainer_VCDetails}>
              <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                <label className={styles.label}>Country Code</label>
              </div>
              <div className={styles.lineModalLeftCustomSelect_BasicInfo}>
                <CustomSelect
                  name="countryCode"
                  isMandatory
                  markIfUnselected={highlightMandatoryFields}
                  options={getCountryCodeOptions()}
                  className={clsx(styles.select, styles.sel1)}
                  value={countryCode}
                  onChange={(selected) => setCountryCode(selected)}
                />
              </div>
              <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                <label className={styles.label}>Colour Code</label>
              </div>
              <div className={styles.lineModalLeftCustomSelect_BasicInfo}>
                <CustomSelect
                  name="colourCode"
                  isMandatory
                  markIfUnselected={highlightMandatoryFields}
                  options={getColourOptions()}
                  className={clsx(styles.select, styles.sel1)}
                  value={colourCode}
                  onChange={(selected) => setColourCode(selected)}
                />
              </div>
            </div>
          </Box>
        </div>

        <div className={styles.bottomRow}>
          <button
            type="button"
            className={styles.cancelBt}
            onClick={handleClose}
          >
            {cancelButtonText}
          </button>
          <button
            type="button"
            onClick={(e) => {
              if (e.detail > 1) return;
              runPreSubmissionChecks() && handleSubmit();
            }}
            className={styles.submitBt}
          >
            {submitButtonText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

AddEvrNumberForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  plant: PropTypes.string.isRequired,
  lineBackgroundColor: PropTypes.string.isRequired,
  cancelButtonText: PropTypes.string,
  submitButtonText: PropTypes.string,
};

export default AddEvrNumberForm;